import { AlertCircle, ArrowLeft, Building2, Plus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { useOrganizationsPortfolio } from "@/modules/dashboard/clients/hooks/useOrganizationsPortfolio";
import { WalletTopUpDialog } from "@/modules/dashboard/wallet-top-ups/components/WalletTopUpDialog";
import { useWalletTopUpAccess } from "@/modules/dashboard/wallet-top-ups/hooks/useWalletTopUpAccess";
import type { WalletTopUpTarget } from "@/modules/dashboard/wallet-top-ups/types/walletTopUps.types";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSearchInput } from "@/shared/components/forms/ColocaSearchInput";
import { ColocaPagination } from "@/shared/components/navigation/ColocaPagination";
import { NoAccessView } from "@/shared/components/NoAccessView";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { isActivatableWalletCurrency } from "../constants/wallets.constants";
import { useRuntimeWalletActivity } from "../hooks/useRuntimeWalletActivity";
import { useRuntimeWallets } from "../hooks/useRuntimeWallets";
import type { RuntimeWallet } from "../types/payments-wallets.types";
import { ActivateWalletModal } from "./ActivateWalletModal";
import { ActiveWalletCard } from "./ActiveWalletCard";
import { WalletsOverview } from "./overview/WalletsOverview";

interface SelectedOrg {
  id: string;
  name: string;
}

type ReaderView =
  | { kind: "overview" }
  | { kind: "picker" }
  | { kind: "org"; org: SelectedOrg; from: "overview" | "picker" };

/**
 * INTERNAL-only surface for the wallets home. It opens on the overview of every client's wallets (totals per
 * currency, filters, ranking); from there staff open one organization to read (view only) its wallets, or
 * pick any organization — including one without wallets yet — to activate one on its behalf. Internal staff
 * are not scoped to their own org, so a target org is always explicit (handoff §5).
 *
 * The overview stays mounted while another view is open, so coming back keeps its filters, page and scroll;
 * it re-reads on return, since the organization view can move money (top-ups, activations, freezes).
 */
export const OrgWalletsReader = () => {
  const { t } = useTranslations("wallets");
  const [view, setView] = useState<ReaderView>({ kind: "overview" });
  const [reloadSignal, setReloadSignal] = useState(0);

  const backToOverview = () => {
    setReloadSignal((signal) => signal + 1);
    setView({ kind: "overview" });
  };

  return (
    <>
      <div className={cn(view.kind !== "overview" && "hidden")}>
        <WalletsOverview
          reloadSignal={reloadSignal}
          onOpenOrganization={(org) => setView({ kind: "org", org, from: "overview" })}
          onActivateForOrganization={() => setView({ kind: "picker" })}
        />
      </div>
      {view.kind === "picker" && (
        <OrgPicker
          onSelect={(org) => setView({ kind: "org", org, from: "picker" })}
          onBack={() => setView({ kind: "overview" })}
        />
      )}
      {view.kind === "org" && (
        <OrgWalletsPanel
          org={view.org}
          backLabel={view.from === "picker" ? t("reader_change_org") : t("overview_back")}
          onBack={() => (view.from === "picker" ? setView({ kind: "picker" }) : backToOverview())}
        />
      )}
    </>
  );
};

interface OrgPickerProps {
  onSelect: (org: SelectedOrg) => void;
  onBack: () => void;
}

const OrgPicker = ({ onSelect, onBack }: OrgPickerProps) => {
  const { t } = useTranslations("wallets");
  const portfolio = useOrganizationsPortfolio();

  if (portfolio.noAccess) {
    return <NoAccessView />;
  }

  return (
    <div className="space-y-4">
      <div>
        <ColocaButton variant="ghost" size="sm" icon={ArrowLeft} onClick={onBack} className="mb-1 -ml-2">
          {t("overview_back")}
        </ColocaButton>
        <h2 className="text-base font-semibold text-foreground">{t("reader_title")}</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">{t("reader_subtitle")}</p>
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          portfolio.applyFilters();
        }}
      >
        <ColocaSearchInput
          value={portfolio.filters.search}
          onValueChange={(value) => portfolio.setFilters((prev) => ({ ...prev, search: value }))}
          placeholder={t("reader_search_placeholder")}
        />
      </form>

      {portfolio.error ? (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          text={portfolio.error}
          actions={
            <ColocaButton variant="secondary" size="sm" onClick={() => portfolio.refetch()}>
              {t("retry")}
            </ColocaButton>
          }
        />
      ) : portfolio.isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : portfolio.rows.length === 0 ? (
        <ColocaEmpty icon={Building2} title={t("reader_no_orgs")} />
      ) : (
        <>
          <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {portfolio.rows.map((org) => (
              <li key={org.id}>
                <button
                  type="button"
                  onClick={() => onSelect({ id: org.id, name: org.name })}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40 focus:bg-muted/40 focus:outline-none"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-foreground">{org.name}</span>
                    {org.email && (
                      <span className="block truncate text-xs text-muted-foreground">{org.email}</span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {portfolio.total > portfolio.pageSize && (
            <ColocaPagination
              page={portfolio.page}
              perPage={portfolio.pageSize}
              total={portfolio.total}
              onPageChange={portfolio.setPage}
            />
          )}
        </>
      )}
    </div>
  );
};

interface OrgWalletsPanelProps {
  org: SelectedOrg;
  onBack: () => void;
  backLabel: string;
}

const OrgWalletsPanel = ({ org, onBack, backLabel }: OrgWalletsPanelProps) => {
  const { t } = useTranslations("wallets");
  const {
    wallets: allWallets,
    isLoading,
    hasAccess,
    error,
    refresh,
    refreshOne: refreshOneWallet,
  } = useRuntimeWallets(org.id);
  // PROVISIONAL — same gate as the member view (`ACTIVATABLE_WALLET_CURRENCIES`). Staff read this surface to
  // answer a client's question about it, so showing them a wallet the client cannot see would have the two
  // halves of a support conversation looking at different lists.
  const wallets = useMemo(
    () => allWallets.filter((wallet) => isActivatableWalletCurrency(wallet.currency)),
    [allWallets],
  );
  const {
    activityByWallet,
    isLoadingActivity,
    refreshOne: refreshOneActivity,
  } = useRuntimeWalletActivity(wallets);

  // Same rule as the member view: a card's refresh reads that wallet only. The header button above still
  // re-reads the whole org.
  const refreshWallet = useCallback(
    async (walletId: string) => {
      await Promise.all([refreshOneWallet(walletId), refreshOneActivity(walletId)]);
    },
    [refreshOneWallet, refreshOneActivity],
  );
  const [isActivateOpen, setIsActivateOpen] = useState(false);
  // Unfiltered, so activating on the client's behalf cannot offer a currency they already hold.
  const activatedCurrencies = useMemo(() => allWallets.map((w) => w.currency), [allWallets]);
  const { allowed: canTopUp } = useWalletTopUpAccess();
  const [topUpTarget, setTopUpTarget] = useState<WalletTopUpTarget | null>(null);
  // Reached only from a rendered card, so the activation gate already bounds it: a withheld currency has no
  // card and therefore cannot be topped up from here. That is the intended reading of the gate, not a side
  // effect — `/app/wallets` offers nothing for a wallet it does not show.
  const openTopUp = useCallback(
    (wallet: RuntimeWallet) => setTopUpTarget({ organization: org, walletId: wallet.id }),
    [org],
  );

  // The runtime denies with 401/403 when the INTERNAL user lacks `payments:read` — same latch as the home.
  if (!hasAccess) {
    return <NoAccessView />;
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <ColocaButton variant="ghost" size="sm" icon={ArrowLeft} onClick={onBack} className="mb-1 -ml-2">
            {backLabel}
          </ColocaButton>
          <h2 className="text-base font-semibold text-foreground">{org.name}</h2>
        </div>
        {/* The empty state below carries the same action as the primary; this one steps back while it shows. */}
        <ColocaButton
          variant={!isLoading && !error && wallets.length === 0 ? "default" : "primary"}
          size="sm"
          icon={Plus}
          onClick={() => setIsActivateOpen(true)}
        >
          {t("activate_cta")}
        </ColocaButton>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : error ? (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          text={error}
          actions={
            <ColocaButton variant="secondary" size="sm" onClick={() => void refresh()}>
              {t("retry")}
            </ColocaButton>
          }
        />
      ) : wallets.length === 0 ? (
        <ColocaEmpty
          icon={Building2}
          title={t("reader_org_wallets_empty")}
          description={t("empty_activate_desc")}
        >
          <ColocaButton variant="primary" icon={Plus} onClick={() => setIsActivateOpen(true)}>
            {t("empty_activate_cta")}
          </ColocaButton>
        </ColocaEmpty>
      ) : (
        <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2 xl:grid-cols-3">
          {wallets.map((wallet) => (
            <ActiveWalletCard
              key={wallet.id}
              wallet={wallet}
              recent={activityByWallet[wallet.id] ?? []}
              isLoadingRecent={isLoadingActivity}
              onRefresh={refreshWallet}
              readOnly
              onTopUp={canTopUp ? openTopUp : undefined}
            />
          ))}
        </div>
      )}

      <WalletTopUpDialog
        target={topUpTarget}
        onClose={() => setTopUpTarget(null)}
        onCreated={(topUp) => void refreshWallet(topUp.wallet_id)}
      />

      <ActivateWalletModal
        open={isActivateOpen}
        onClose={() => setIsActivateOpen(false)}
        activatedCurrencies={activatedCurrencies}
        onActivated={refresh}
        organizationId={org.id}
      />
    </div>
  );
};
